import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const INTERNAL_CHANNEL = 'Channel::Internal';

const internalMeta = conversation =>
  conversation?.additional_attributes?.internal || {};

// `meta.channel` (set by `app/views/api/v1/conversations/partials/_conversation.json.jbuilder`)
// is the only place where the channel type lives in the serialized conversation
// payload — there's no nested `inbox` object. We fall back to the inboxes store
// in case some caller hands us a conversation built outside the standard
// serializer (e.g. push events from older clients).
const isInternalConversation = (conversation, inboxLookup) => {
  if (!conversation) return false;
  if (conversation?.inbox?.channel_type === INTERNAL_CHANNEL) return true;
  if (conversation?.meta?.channel === INTERNAL_CHANNEL) return true;
  if (conversation?.inbox_id && inboxLookup) {
    return inboxLookup(conversation.inbox_id)?.channel_type === INTERNAL_CHANNEL;
  }
  return false;
};

const resolveAgent = (agents, userId) =>
  agents.find(agent => Number(agent.id) === Number(userId));

const buildTitlePayload = ({ conversation, currentUserId, agents, joinWith }) => {
  const meta = internalMeta(conversation);

  if (meta.kind === 'room') {
    return {
      name: meta.group_name || '',
      avatarUrl: null,
      availability: null,
      isGroup: true,
    };
  }

  const participantIds = (meta.participant_user_ids || []).map(Number);
  const currentId = Number(currentUserId);
  const isParticipant = participantIds.includes(currentId);

  // Observer view (auditor / admin not in the DM): show every participant's
  // name joined together so the auditor knows whose conversation this is.
  if (!isParticipant) {
    const names = participantIds
      .map(id => resolveAgent(agents, id))
      .filter(Boolean)
      .map(agent => agent.name || agent.available_name)
      .filter(Boolean);
    return {
      name: names.join(joinWith),
      avatarUrl: null,
      availability: null,
      isGroup: false,
      isObserver: true,
    };
  }

  const peerId = participantIds.find(id => id !== currentId);
  const peer = peerId ? resolveAgent(agents, peerId) : null;
  return {
    name: peer?.name || peer?.available_name || '',
    avatarUrl: peer?.thumbnail || peer?.avatar_url || null,
    availability: peer?.availability_status || 'offline',
    isGroup: false,
    peerUserId: peerId,
  };
};

export const useInternalConversationTitle = conversationRef => {
  const store = useStore();
  const { t } = useI18n();
  const currentUser = useMapGetter('getCurrentUser');
  const agents = useMapGetter('agents/getAgents');
  const inboxGetter = useMapGetter('inboxes/getInbox');

  const conversation = computed(() => {
    if (!conversationRef) return null;
    if (typeof conversationRef === 'function') return conversationRef();
    return conversationRef.value ?? conversationRef;
  });

  const isInternal = computed(() =>
    isInternalConversation(conversation.value, inboxGetter.value)
  );

  const title = computed(() => {
    if (!isInternal.value) return null;
    return buildTitlePayload({
      conversation: conversation.value,
      currentUserId: currentUser.value?.id,
      agents: agents.value || [],
      joinWith: t('INTERNAL_CHAT.OBSERVER_PARTICIPANT_JOIN'),
    });
  });

  const ensureAgentsLoaded = () => {
    if (!agents.value?.length) {
      store.dispatch('agents/get');
    }
  };

  return {
    title,
    isInternal,
    ensureAgentsLoaded,
  };
};

export { isInternalConversation, internalMeta };
